// LiquidationDashboard.jsx
// Dependencies: react, axios, luxon, recharts, tailwindcss

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { DateTime } from "luxon";

/* -------------------- COLORS -------------------- */
const COLORS = {
  buyout: "#16A34A", // green-600
  buyoutRevenue: "#15803D", // green-700
  returnSel: "#2563EB", // blue-600
  selectionMade: "#7C3AED", // violet-600
  allowedBuyout: "#F59E0B", // amber-500
  notAllowedBuyout: "#DC2626", // red-600
  status: [
    "#0EA5E9",
    "#8B5CF6",
    "#22C55E",
    "#EF4444",
    "#F97316",
    "#06B6D4",
    "#A3E635",
    "#9333EA",
    "#84CC16",
    "#FB7185",
  ],
  grid: "#E2E8F0",
  axis: "#4B5563",
};

/* -------------------- HELPERS -------------------- */
const fmtNOK0 = (n) =>
  typeof n === "number"
    ? n.toLocaleString("no-NO", {
        style: "currency",
        currency: "NOK",
        maximumFractionDigits: 0,
      })
    : "—";
const fmtDate = (d) => (d ? DateTime.fromISO(d).toFormat("yyyy-LL-dd") : "—");

const toDT = (v) => {
  if (!v) return null;
  try {
    return typeof v === "string"
      ? DateTime.fromISO(v)
      : DateTime.fromJSDate(new Date(v));
  } catch {
    return null;
  }
};

// Make a bucket key by granularity
function bucketKey(dt, granularity) {
  if (!dt || !dt.isValid) return "invalid";
  switch (granularity) {
    case "weekly":
      return `${dt.weekNumber}-${dt.year}`;
    case "monthly":
      return `${dt.month}-${dt.year}`;
    default:
      return dt.toISODate();
  }
}

// Beginning-of-bucket for consistent x-axis labels
function bucketStart(dt, granularity) {
  switch (granularity) {
    case "weekly":
      return dt.startOf("week");
    case "monthly":
      return dt.startOf("month");
    default:
      return dt.startOf("day");
  }
}

/* -------------------- KPI CARD -------------------- */
function KpiCard({ title, value, sub }) {
  return (
    <div className="flex flex-col bg-white border rounded-lg shadow-sm p-4 w-full">
      <span className="text-sm font-medium text-gray-500 mb-1">{title}</span>
      <span className="text-2xl font-bold text-gray-800 leading-none">
        {value}
      </span>
      {sub && (
        <span className="text-xs font-medium text-gray-500 mt-1">{sub}</span>
      )}
    </div>
  );
}

/* -------------------- MAIN -------------------- */
export default function LiquidationDashboard() {
  const [inventory, setInventory] = useState([]);
  const [granularity, setGranularity] = useState("daily"); // daily | weekly | monthly
  const [startDate, setStartDate] = useState(
    DateTime.local().minus({ days: 60 }).toISODate()
  );
  const [endDate, setEndDate] = useState(DateTime.local().toISODate());
  const [loading, setLoading] = useState(false);

  // Fetch inventory once
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get("/api/inventory");
        setInventory(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching inventory", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Derived arrays
  const buyouts = useMemo(
    () =>
      inventory.filter(
        (b) =>
          b?.customerSelectionType === "buyout" ||
          (b?.buyout?.termsAccepted && b?.buyout?.purchaseDate) // safety net
      ),
    [inventory]
  );
  const returnsChosen = useMemo(
    () => inventory.filter((b) => b?.customerSelectionType === "return"),
    [inventory]
  );
  const selectionsMade = useMemo(
    () => inventory.filter((b) => b?.customerSelectionMade),
    [inventory]
  );

  // Date filtering for time series (buyouts use buyout.purchaseDate)
  const filteredBuyouts = useMemo(() => {
    const s = DateTime.fromISO(startDate);
    const e = DateTime.fromISO(endDate);
    return buyouts.filter((b) => {
      const dt = toDT(b?.buyout?.purchaseDate);
      return dt && dt.isValid && dt >= s.startOf("day") && dt <= e.endOf("day");
    });
  }, [buyouts, startDate, endDate]);

  // Bucket buyouts by date granularity
  const buyoutSeries = useMemo(() => {
    const buckets = new Map();
    for (const b of filteredBuyouts) {
      const dt = bucketStart(toDT(b?.buyout?.purchaseDate), granularity);
      if (!dt) continue;
      const key = bucketKey(dt, granularity);
      if (!buckets.has(key))
        buckets.set(key, {
          key,
          date: dt, // DateTime for sorting/label
          count: 0,
          revenueInc: 0,
          revenueEx: 0,
        });
      const row = buckets.get(key);
      row.count += 1;
      row.revenueInc += Number(b?.buyout?.purchasePriceIncVat || 0);
      row.revenueEx += Number(b?.buyout?.purchasePriceExVat || 0);
    }
    const arr = Array.from(buckets.values()).sort((a, b) => a.date - b.date);
    // Add cumulative count
    let cum = 0;
    return arr.map((r) => {
      cum += r.count;
      return {
        dateLabel:
          granularity === "daily"
            ? r.date.toISODate()
            : granularity === "weekly"
            ? `WK ${r.date.weekNumber} ${r.date.year}`
            : r.date.toFormat("yyyy-LL"),
        count: r.count,
        cumCount: cum,
        revenueInc: r.revenueInc,
        revenueEx: r.revenueEx,
      };
    });
  }, [filteredBuyouts, granularity]);

  // KPI numbers
  const kpis = useMemo(() => {
    const total = inventory.length;

    const allowed = inventory.filter((b) => b?.allowedBuyout !== false).length;
    const notAllowed = total - allowed;

    const selMade = selectionsMade.length;
    const selBuyout = buyouts.length;
    const selReturn = returnsChosen.length;

    const totalRevenueInc = buyouts.reduce(
      (t, b) => t + Number(b?.buyout?.purchasePriceIncVat || 0),
      0
    );
    const avgPriceInc = selBuyout > 0 ? totalRevenueInc / selBuyout : 0;

    return {
      total,
      allowed,
      notAllowed,
      selMade,
      selBuyout,
      selReturn,
      totalRevenueInc,
      avgPriceInc,
    };
  }, [inventory, selectionsMade, buyouts, returnsChosen]);

  // Operational status distribution (current point-in-time)
  const statusDist = useMemo(() => {
    const counts = {};
    inventory.forEach((b) => {
      const s = b?.status?.operationalStatus || "unknown";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [inventory]);

  // Latest buyouts table
  const latestBuyouts = useMemo(() => {
    const arr = [...buyouts].sort((a, b) => {
      const da = toDT(a?.buyout?.purchaseDate)?.toMillis?.() || 0;
      const db = toDT(b?.buyout?.purchaseDate)?.toMillis?.() || 0;
      return db - da;
    });
    return arr.slice(0, 25); // show latest 25
  }, [buyouts]);

  return (
    <div className="w-full min-h-screen px-4 pt-8 pb-11 bg-gray-50">
      <div className="bg-white shadow-sm border rounded-lg p-6 space-y-12 h-full">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Liquidation Dashboard
          </h1>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-col">
              <label
                htmlFor="granularity"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Granularity
              </label>
              <select
                id="granularity"
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="startDate"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="endDate"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
          <KpiCard title="Total Bikes" value={kpis.total.toLocaleString()} />
          <KpiCard
            title="Allowed Buyout"
            value={kpis.allowed.toLocaleString()}
          />
          <KpiCard
            title="Not Allowed"
            value={kpis.notAllowed.toLocaleString()}
          />
          <KpiCard
            title="Selection Made"
            value={kpis.selMade.toLocaleString()}
          />
          <KpiCard title="Buyouts" value={kpis.selBuyout.toLocaleString()} />
          <KpiCard
            title="Returns (chosen)"
            value={kpis.selReturn.toLocaleString()}
          />
          <KpiCard
            title="Revenue (Inc. VAT)"
            value={fmtNOK0(kpis.totalRevenueInc)}
          />
          <KpiCard
            title="Avg Buyout Price"
            value={fmtNOK0(kpis.avgPriceInc)}
            sub={kpis.selBuyout > 0 ? `${kpis.selBuyout} bikes` : "—"}
          />
        </div>

        {/* Time series: Buyouts & Revenue */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Buyouts Over Time ({granularity})
          </h2>
          <div className="w-full h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={buyoutSeries}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                <XAxis
                  dataKey="dateLabel"
                  stroke={COLORS.axis}
                  interval="preserveStartEnd"
                />
                <YAxis yAxisId="left" stroke={COLORS.buyout} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={COLORS.buyoutRevenue}
                />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="count"
                  stroke={COLORS.buyout}
                  strokeWidth={2}
                  dot={false}
                  name="Buyouts"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="cumCount"
                  stroke="#111827"
                  strokeDasharray="4 4"
                  dot={false}
                  name="Cumulative"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revenueInc"
                  stroke={COLORS.buyoutRevenue}
                  strokeWidth={2}
                  dot={false}
                  name="Revenue (Inc. VAT)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Selection mix & status distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Selection Mix
            </h2>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    {
                      label: "Current",
                      buyout: kpis.selBuyout,
                      returnSel: kpis.selReturn,
                      selectionMade: kpis.selMade,
                      allowed: kpis.allowed,
                      notAllowed: kpis.notAllowed,
                    },
                  ]}
                  margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                  <XAxis dataKey="label" stroke={COLORS.axis} />
                  <YAxis stroke={COLORS.axis} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="buyout" fill={COLORS.buyout} name="Buyout" />
                  <Bar
                    dataKey="returnSel"
                    fill={COLORS.returnSel}
                    name="Return (chosen)"
                  />
                  <Bar
                    dataKey="selectionMade"
                    fill={COLORS.selectionMade}
                    name="Any selection"
                  />
                  <Bar
                    dataKey="allowed"
                    fill={COLORS.allowedBuyout}
                    name="Allowed buyout"
                  />
                  <Bar
                    dataKey="notAllowed"
                    fill={COLORS.notAllowedBuyout}
                    name="Not allowed"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Operational Status Distribution
            </h2>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip />
                  <Legend />
                  <Pie
                    data={statusDist}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={110}
                    label={(e) => `${e.name} (${e.value})`}
                  >
                    {statusDist.map((_, i) => (
                      <Cell
                        key={i}
                        fill={COLORS.status[i % COLORS.status.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>

        {/* Revenue area (smooth) */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Revenue (Inc. VAT) by {granularity}
          </h2>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={buyoutSeries}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
                <XAxis
                  dataKey="dateLabel"
                  stroke={COLORS.axis}
                  interval="preserveStartEnd"
                />
                <YAxis stroke={COLORS.axis} />
                <Tooltip formatter={(v) => fmtNOK0(v)} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="revenueInc"
                  stroke={COLORS.buyoutRevenue}
                  fill={COLORS.buyoutRevenue}
                  name="Revenue (Inc. VAT)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Latest Buyouts Table */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Latest Buyouts
          </h2>
        </section>
        <div className="overflow-x-auto max-h-[500px] border rounded-lg">
          <table className="min-w-full text-sm text-gray-700 divide-y divide-gray-200">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Date</th>
                <th className="px-3 py-2 text-left font-semibold">Serial</th>
                <th className="px-3 py-2 text-left font-semibold">Bike</th>
                <th className="px-3 py-2 text-left font-semibold">
                  Price Inc.
                </th>
                <th className="px-3 py-2 text-left font-semibold">Price Ex.</th>
                <th className="px-3 py-2 text-left font-semibold">Invoice</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {latestBuyouts.length ? (
                latestBuyouts.map((b) => (
                  <tr key={b.serialNumber} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {fmtDate(b?.buyout?.purchaseDate)}
                    </td>
                    <td className="px-3 py-2">{b.serialNumber}</td>
                    <td className="px-3 py-2">
                      {[b?.model?.brand, b?.model?.type, b?.model?.size]
                        .filter(Boolean)
                        .join(" ")}
                    </td>
                    <td className="px-3 py-2">
                      {fmtNOK0(b?.buyout?.purchasePriceIncVat)}
                    </td>
                    <td className="px-3 py-2">
                      {fmtNOK0(b?.buyout?.purchasePriceExVat)}
                    </td>
                    <td className="px-3 py-2">
                      {b?.buyout?.invoiceNumber || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {b?.status?.operationalStatus || "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-4 text-center text-gray-500"
                  >
                    No buyouts in the selected range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-sm text-gray-500">Fetching inventory…</div>
        )}
      </div>
    </div>
  );
}
